import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { categoryColor } from '@/shared/lib/category-color';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Plus } from 'lucide-react';
import { ScripsIcon, type ScripsIconName } from '@/shared/ui/components';
import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';
import type { QuickBookGroup } from './provider-card';

/**
 * QuickBookChip — Component
 * One quick-book chip on the provider strip. Clicking the chip opens a
 * popover listing every appointment profile in the same location group
 * (Flutter parity — `quickBookCardView` in calendar_tab.dart:4717).
 *
 * The chip's leading glyph is rendered via the Scrips icon font so the
 * tile shape (filled square with white center) matches dev. The
 * `group.color` is the parsed CSS color from `appointmentProfile.color`
 * — applied as the glyph foreground; the Scrips font's location glyphs
 * already include the tile shape.
 */

const LOCATION_ICONS: Record<string, ScripsIconName> = {
  office: 'office',
  home: 'home',
  virtual: 'virtual',
  block: 'block',
};

const POPOVER_OFFSET = 8;

interface QuickBookChipProps {
  group: QuickBookGroup;
  onSelectProfile?: (profile: CreateAppointmentProfileRequest) => void;
  /**
   * 'inline' (default): the glyph in the profile's own colour and the duration, as text.
   * 'tile': the practitioner badge's look (Antonin, 13 Oct): a soft tile in the location's CATEGORY colour (the muted
   * Okabe-Ito ramp, `--cat-*`): in clinic jade, video sky, home orange, block purple. Icon 600, label 800, tint 100.
   */
  variant?: 'inline' | 'tile';
}

/** Booking location → its category hue (the muted Okabe-Ito ramp; colour for categories, never status). */
const LOCATION_HUE: Record<string, string> = { office: 'jade', virtual: 'sky', home: 'orange', block: 'purple' };
const LOCATION_LABEL: Record<string, string> = { office: 'In clinic', virtual: 'Video', home: 'Home', block: 'Block' };

function iconFor(name: string | undefined): ScripsIconName {
  // Use a real ternary so an empty-string `name` falls through to the
  // 'office' default — `name && LOCATION_ICONS[...]` would return ""
  // (falsy but typed as string), making the return type "" | ScripsIconName.
  if (!name) return 'office';
  return LOCATION_ICONS[name.toLowerCase()] ?? 'office';
}

export function QuickBookChip({
  group,
  onSelectProfile,
  variant = 'inline',
}: QuickBookChipProps) {
  const loc = (group.location || 'office').toLowerCase();
  const hue = LOCATION_HUE[loc] ?? 'jade';
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !chipRef.current) return;
    const rect = chipRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + POPOVER_OFFSET,
      left: rect.left,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (chipRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const popover =
    open && coords
      ? createPortal(
          <div
            ref={popoverRef}
            role="menu"
            aria-label={`${group.location} profiles`}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
            }}
            className="z-[100] min-w-[304px] bg-surface-card border border-border-default rounded-lg shadow-lg p-2"
          >
            <div className="px-1 pb-1 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
              {LOCATION_LABEL[loc] ?? group.location}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {group.profiles.map((profile, i) => {
                const profileIcon = iconFor(
                  profile.location?.name ?? group.location,
                );
                const isPreferred = profile.isPreferred === true;
                return (
                  <button
                    key={profile.id ?? `${profile.profileName}-${i}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectProfile?.(profile);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 p-2 rounded border border-border-subtle hover:bg-grey-100 text-left transition-colors"
                    role="menuitem"
                  >
                    {/* Icon column — location glyph with the preferred star
                        stacked beneath it, mirroring Flutter
                        calendar_tab.dart:4880-4900. */}
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <ScripsIcon
                        name={profileIcon}
                        size={15}
                        style={profile.color ? { color: categoryColor(profile.color, 600) } : undefined}
                      />
                      {isPreferred && (
                        <ScripsIcon
                          name="starFilled"
                          size={11}
                          style={{ color: 'var(--color-yellow-500)' }}
                          aria-label="Preferred"
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium text-text-primary truncate block">
                        {profile.profileName}
                      </span>
                      <div className="text-xs text-text-muted truncate">
                        {profile.speciality?.display ?? profile.appointmentType?.display ?? ''}
                      </div>
                      <div className="text-xs text-text-primary">
                        {profile.minutesDuration ?? 0}m
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            setOpen((v) => !v);
          }
          if (e.key === 'Escape') setOpen(false);
        }}
        className={cn(
          variant === 'tile'
            // Interactive, visibly (Antonin, 13 Oct: "has no hover, and doesn't look clickable"): the tint deepens a step,
            // the edge takes the category colour and the tile lifts on hover; it settles on press; a ring on keyboard
            // focus; the "+" in the corner says it adds a booking. Colours come in as --tile-* (the location's hue).
            ? 'group/tile relative flex w-full min-w-0 cursor-pointer flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition-[background-color,border-color,box-shadow,transform] duration-150 bg-[var(--tile-bg)] border-[var(--tile-line)] hover:-translate-y-px hover:bg-[var(--tile-bg-hover)] hover:border-[var(--tile-edge)] hover:shadow-[0_4px_12px_rgba(21,27,32,0.10)] active:translate-y-0 active:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tile-edge)] focus-visible:ring-offset-1 aria-expanded:border-[var(--tile-edge)] aria-expanded:bg-[var(--tile-bg-hover)]'
            : 'flex items-center gap-1 text-xs text-text-muted hover:text-brand-primary cursor-pointer',
        )}
        style={variant === 'tile' ? ({
          '--tile-bg': `var(--cat-${hue}-100)`,
          '--tile-bg-hover': `var(--cat-${hue}-200)`,
          '--tile-line': `color-mix(in srgb, var(--cat-${hue}-200) 70%, transparent)`,
          '--tile-edge': `var(--cat-${hue}-500)`,
          color: `var(--cat-${hue}-800)`,
        } as React.CSSProperties) : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        title={group.profiles.map((p) => p.profileName).join(' · ')}
      >
        <ScripsIcon
          name={iconFor(group.location)}
          size={variant === 'tile' ? 16 : 14}
          style={variant === 'tile' ? { color: `var(--cat-${hue}-600)` } : group.color ? { color: categoryColor(group.color, 600) } : undefined}
        />
        {variant === 'tile' && (
          <Plus aria-hidden className="absolute right-2 top-2 h-4 w-4 opacity-60 transition-opacity group-hover/tile:opacity-100" style={{ color: `var(--cat-${hue}-600)` }} />
        )}
        {variant === 'tile' ? (
          // Stacked so nothing clips at a third of the badge: the label, then the length under it.
          <span className="flex min-w-0 max-w-full flex-col">
            <span className="truncate text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)]">{LOCATION_LABEL[loc] ?? group.location}</span>
            <span className="truncate text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] opacity-80">{group.durationMinutes} min</span>
          </span>
        ) : `${group.durationMinutes}m`}
      </button>
      {popover}
    </>
  );
}
